import React, { useState, useEffect } from 'react';
import {
    Autocomplete,
    Box,
    IconButton,
    FormControl,
    FormHelperText,
    TextField,
    Typography,
    Slider,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import config from '../../js/config';
import { getBearerString } from '../../js/jwt_token';


type AnalyticFilterBoxProps = {
    analyticsList: string[];
    openFilterValues: any;
    onDelete: () => void;
    onChange: (analyticObj: any) => void;
}

const AnalyticFilterBox = ({ analyticsList, openFilterValues, onDelete, onChange }: AnalyticFilterBoxProps) => {
    const [selectedObj, setSelectedObj] = useState(null); // This is the selectedObj in the dropdown
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        console.log('Analytics List:', analyticsList);
        if (selectedObj === null && (Object.keys(openFilterValues).length !== 0)) {
            console.log('OpenFilterValues: ', openFilterValues);
            setSelectedObj(openFilterValues);
            updateSelectedObj(openFilterValues);
        }
    }, [selectedObj]);

    const updateSelectedObj = async (analyticObject) => {
        // Function Handles Combining Past Filter Values (onOpenFilterValues) w/ the API Filter Values
        // If a filter field isn't present in the onOpenFilterValues it will use the one from the api call

        // Set name and version based on either selectedObj or onOpenObj
        const name = analyticObject.name;
        const version = analyticObject.version;

        // Fetch the default filter fields and value ranges when an object is selected
        const response = await fetch(config.data_platform_api_url + `/logs/analytic_filter_options?name=${name}&version=${version}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': getBearerString()
            }
          });
        const data = await response.json();

        // Set Filters Values to Selected Object if not undefined -> onOpenObj if not empty {} then empty dict
        const filters = analyticObject.filters || {};

        console.log('Data:', data);

        // Check if filters is empty (i.e., no keys) and then populate it
        if (Object.keys(filters).length === 0) {


            // Check that it isn't null
            if (data.range_values) {
                Object.entries(data.range_values).forEach(([key, val]) => {
                    filters[key] = val;
                })
            }

            if (data.distinct_value) {
                Object.entries(data.distinct_value).forEach(([key, val]) => {
                    filters[key] = [];
                })
            }
        }

        const newSelectedObj = {
            ...data,
            filters: filters
        }

        setSelectedObj(newSelectedObj);
        onChange(newSelectedObj);
    }

    const handleObjChange = (event, obj) => {
        if (obj == null) {
            setSelectedObj(null)
            onChange(null);
            return
        }

        setLoading(true);
        try {
            updateSelectedObj(obj);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch filter fields:', error);
            setLoading(false);
        }
        onChange(obj);
    };


    const handleFilterChange = (field, value) => {
        const newSelectedObj = {
            ...selectedObj,
            filters: {
                ...selectedObj.filters,
                [field]: value,
            }
        }

        setSelectedObj(newSelectedObj);
        onChange(newSelectedObj);
    };

    return (
        <Box mb={2} border={1} borderRadius={4} p={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                {/* Dropdown based on AnalyticsList of Analytics (Dict Obj)*/}
                <Autocomplete
                    options={analyticsList}
                    value={selectedObj}
                    onChange={handleObjChange}
                    getOptionLabel={(option) => `${option.name.replace(/_/g, ' ')} v${option.version}`}
                    renderInput={(params) => (
                        <TextField {...params} label="Analytic Filter Object" variant="outlined" />
                    )}
                    isOptionEqualToValue={(option, value) => option.name === value.name && option.version === value.version} // Custom equality check
                    style={{ flexGrow: 1, marginRight: '1rem' }} // Allow dropdown to take available space
                />
                {/* Delete Button */}
                <IconButton onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                selectedObj && selectedObj?.additional_keys_values_type && Object.keys(selectedObj.additional_keys_values_type).length > 0 && (
                    <Box mt={2}>
                        <Typography variant="subtitle1">Filter Fields</Typography>
                        {selectedObj.additional_keys.map((key) => (
                            <Box key={key} mb={2}>
                                <Typography gutterBottom>{key.replace(/_/g, ' ')}</Typography>
                                {selectedObj.additional_keys_values_type[key] === 'RANGE' && (
                                    <Slider
                                        value={selectedObj?.filters && selectedObj?.filters[key] && selectedObj.filters[key] || [selectedObj.min[key], selectedObj.max[key]]}
                                        min={selectedObj.range_values[key][0]}
                                        max={selectedObj.range_values[key][1]}
                                        step={0.01}
                                        valueLabelDisplay="auto"
                                        onChange={(e, value) => handleFilterChange(key, value)}
                                    />
                                )}
                                {selectedObj.additional_keys_values_type[key].includes('DISTINCT') && (
                                    <FormControl fullWidth margin="normal">
                                        <Autocomplete
                                            multiple
                                            options={selectedObj.distinct_values[key].sort((a, b) => a.localeCompare(b))} // Inline sorting
                                            value={selectedObj?.filters && selectedObj?.filters[key]}
                                            onChange={(e, value) => handleFilterChange(key, value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder="Select or type..."
                                                />
                                            )}
                                        />
                                        <FormHelperText>Select multiple Log Features or start typing to filter the options.</FormHelperText>
                                    </FormControl>
                                )}
                            </Box>
                        ))}
                    </Box>
                )
            )}
        </Box>
    );
};

export default AnalyticFilterBox;
